import { scaleMoney, sumUsd, usdFromDollars, usdToDollars, type UsdAmount } from '@reflex/shared';
import { RATE_CARD, type PublishedRate } from '@reflex/plugin-api/rate-card';
import type { ChatTokenUsage } from './types.js';

const TOKENS_PER_MTOK = 1_000_000;
const VERCEL_OPENAI_PREFIX = 'vercel/openai/';

/** Resolve the published OpenAI rate behind a direct or AI Gateway Codex model id. */
function codexRate(model: string): PublishedRate | null {
  const modelId = model.startsWith(VERCEL_OPENAI_PREFIX)
    ? model.slice(VERCEL_OPENAI_PREFIX.length)
    : model;
  const rates = RATE_CARD.openai as Record<string, PublishedRate | undefined>;
  return rates[modelId] ?? null;
}

function stageCost(tokens: number, usdPerMtok: number): UsdAmount | null {
  const rate = usdFromDollars(usdPerMtok);
  return rate ? scaleMoney(rate, tokens / TOKENS_PER_MTOK) : null;
}

/**
 * Estimate a native Codex turn from its token counters and the committed
 * OpenAI rate card. Cache reads and writes are disjoint subsets of input;
 * only the ordinary remainder is charged at the full input rate.
 *
 * Unknown models stay unpriced rather than borrowing another model's rate.
 */
export function estimateCodexTurnCostUsd(
  model: string | null | undefined,
  usage: ChatTokenUsage | null | undefined,
): number | null {
  if (!model || !usage) return null;
  const rate = codexRate(model);
  if (!rate) return null;

  const cachedInputTokens = Math.min(usage.inputTokens, usage.cachedInputTokens);
  const cacheWriteInputTokens = Math.min(
    usage.inputTokens - cachedInputTokens,
    usage.cacheWriteInputTokens ?? 0,
  );
  const uncachedInputTokens = Math.max(
    0,
    usage.inputTokens - cachedInputTokens - cacheWriteInputTokens,
  );
  const inputCost = stageCost(uncachedInputTokens, rate.inputUsdPerMtok);
  const cacheReadCost = stageCost(
    cachedInputTokens,
    rate.cacheReadUsdPerMtok ?? rate.inputUsdPerMtok,
  );
  const outputCost = stageCost(usage.outputTokens, rate.outputUsdPerMtok);
  const cacheWriteCost = stageCost(
    cacheWriteInputTokens,
    rate.cacheWriteUsdPerMtok ?? rate.inputUsdPerMtok,
  );
  if (!inputCost || !cacheReadCost || !cacheWriteCost || !outputCost) return null;

  try {
    return usdToDollars(sumUsd([inputCost, cacheReadCost, cacheWriteCost, outputCost]));
  } catch {
    return null;
  }
}
